import { VaultResponse } from 'compliance-api';
import { Avatar, DropdownMenu, DropdownMenuItem, TextWithShortLinks } from 'ui';
import { DotsHorizontalIcon } from 'ui/icons';

type Props = {
  vault: VaultResponse;
  isAdmin: boolean;
  onOpenVault: (vault: VaultResponse) => void;
  menuOptions: DropdownMenuItem[];
};

const VaultsListItem = ({ vault, isAdmin, onOpenVault, menuOptions }: Props) => {
  const normalizedDescription = vault.description
    ? vault.description.replace(/[\r\n]+/g, ' ').replace(/\s{2,}/g, ' ').trim()
    : '';

  return (
    <div className="sm:border sm:rounded-xl border-gray-200 [&:not(:last-child)]:border-b bg-white">
      <div className="pl-2 sm:px-6 py-4 relative w-full grid grid-cols-[1fr_auto] items-center gap-4 overflow-hidden">
        <div className="space-y-1.5 overflow-hidden">
          <button
            type="button"
            onClick={() => onOpenVault(vault)}
            className="text-sm font-medium text-blue-700 hover:underline cursor-pointer truncate"
            title={vault.name}
          >
            {vault.name}
          </button>
          <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-y-2 sm:gap-y-1 text-[12px] leading-[20px] text-gray-500">
            {!!normalizedDescription && (
              <span className="block min-w-0 sm:inline-flex sm:items-center sm:gap-1 sm:pr-3">
                <span className="block">Description:</span>
                <span className="inline-block max-w-[220px] sm:max-w-[360px] truncate align-bottom text-gray-600">
                  <TextWithShortLinks
                    text={normalizedDescription}
                    className="whitespace-nowrap"
                    linkClassName="text-blue-700 hover:underline"
                    addFormat={false}
                  />
                </span>
              </span>
            )}

            {vault.owner && (
              <span
                className={`inline-flex items-center gap-2 whitespace-nowrap ${
                  normalizedDescription ? 'sm:pl-3 pr-3 sm:border-l sm:border-gray-200' : 'pr-3'
                }`}
                title={`${vault.owner.first_name} ${vault.owner.last_name}`}
              >
                <span>Owner</span>
                <Avatar
                  src={vault.owner.avatar_url || undefined}
                  firstName={vault.owner.first_name}
                  lastName={vault.owner.last_name}
                  className="border border-white"
                  size="smMd"
                />
                <span>
                  {vault.owner.first_name} {vault.owner.last_name}
                </span>
              </span>
            )}

            <span className="inline-flex items-center gap-2 sm:pl-3 sm:border-l sm:border-gray-200 whitespace-nowrap">
              <span>Shared with</span>
              <span className="inline-flex -space-x-1.5 items-center">
                {(vault.shared_users || []).slice(0, 5).map(user => (
                  <span key={user.id} title={`${user.first_name} ${user.last_name}`}>
                    <Avatar
                      src={user.avatar_url || undefined}
                      firstName={user.first_name}
                      lastName={user.last_name}
                      className="border border-white"
                      size="smMd"
                    />
                  </span>
                ))}
                {(vault.shared_users?.length || 0) > 5 && (
                  <span className="text-[12px] text-gray-600 bg-gray-200 rounded-full w-6 h-6 inline-flex items-center justify-center border border-white">
                    +{(vault.shared_users?.length || 0) - 5}
                  </span>
                )}
              </span>
              <span>
                {(vault.shared_users?.length || 0) === 1
                  ? '1 user'
                  : `${vault.shared_users?.length || 0} users`}
              </span>
            </span>
          </div>
        </div>
        {isAdmin && (
          <DropdownMenu
            autoPlace
            items={menuOptions}
            button={
              <button className="flex items-center justify-center w-8 h-8 rounded hover:bg-gray-100 transition-colors">
                <img src={DotsHorizontalIcon} alt="Menu" className="w-6 h-6" />
              </button>
            }
            className="w-[160px] divide-white"
          />
        )}
      </div>
    </div>
  );
};

export default VaultsListItem;
